import { Button, Form, Modal, Radio, Select, message } from 'antd';
import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { UserContext } from 'app/context/UserContext/UserContext';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import { ExperienceType } from 'app/typings/experiences';

import { RULES } from '../../Hotels/HotelDetail/Finance/utils';

import './NewExperienceModal.scss';

const { Option } = Select;

type NewExperienceModalForm = {
  experienceType: ExperienceType;
  hotelId?: number;
  isLegacy?: boolean;
};

type NewExperienceModalProps = {
  open: boolean;
  onClose: () => void;
};

export const NewExperienceModal = ({
  open,
  onClose,
}: NewExperienceModalProps) => {
  const { isSuperAdmin } = useContext(UserContext);
  const { enterViewMode } = useContext(ViewEditModeContext);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const { data: hotels, refetch: refetchHotels } = useHotels();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOk = async (values: NewExperienceModalForm) => {
    const isOriginal = values.experienceType === ExperienceType.STAYCATION;

    setIsSubmitting(true);

    try {
      let isLegacy: boolean;

      if (isOriginal && values.isLegacy !== undefined) {
        isLegacy = values.isLegacy;
      } else {
        let freshHotels = hotels;

        try {
          freshHotels = (await refetchHotels()).data ?? hotels;
        } catch {
          message.error('Could not refresh the hotel list, using cached data.');
        }

        isLegacy =
          freshHotels?.find((h) => h.id === values.hotelId)?.isLegacy ?? true;
      }

      enterViewMode();

      const url = isLegacy ? '/activities/legacy/new' : '/activities/new';

      history.push({
        pathname: url,
        search: isOriginal ? `?isOriginal=true` : `?hotelId=${values.hotelId}`,
      });
    } catch {
      message.error('Could not create the activity, please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="Create new activity"
      open={open}
      width={572}
      closable
      destroyOnClose
      onCancel={onClose}
      centered
      className="new-experience-modal"
      footer={[
        <Button onClick={onClose}>No</Button>,
        <Button
          onClick={form.submit}
          type="primary"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Yes
        </Button>,
      ]}
    >
      <Form
        onFinish={handleOk}
        form={form}
        initialValues={{ experienceType: ExperienceType.HOTEL }}
        layout="vertical"
      >
        <FormLineLayout label="Activity type" isRequired isInModal>
          <Form.Item name="experienceType" rules={RULES}>
            <Radio.Group>
              <Radio value="HOTEL">Hotel</Radio>
              <Radio value="STAYCATION" disabled={!isSuperAdmin}>
                Staycation (Originals)
              </Radio>
            </Radio.Group>
          </Form.Item>
        </FormLineLayout>
        {formValues && formValues.experienceType === ExperienceType.HOTEL && (
          <FormLineLayout label="Hotel" isRequired isInModal>
            <Form.Item name="hotelId" rules={RULES}>
              <Select
                placeholder="Select a hotel"
                showSearch
                optionFilterProp="children"
              >
                {hotels?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </FormLineLayout>
        )}
        {formValues &&
          formValues.experienceType === ExperienceType.STAYCATION && (
            <FormLineLayout label="Mode" isRequired isInModal>
              <Form.Item name="isLegacy" rules={RULES}>
                <Radio.Group>
                  <Radio value={true}>Old</Radio>
                  <Radio value={false}>New</Radio>
                </Radio.Group>
              </Form.Item>
            </FormLineLayout>
          )}
      </Form>
    </Modal>
  );
};
